/**
 * Where a streaming Pebbl reply may be cut for display.
 *
 * WHY THE REPLY IS NOT SHOWN AS IT ARRIVES
 * ----------------------------------------
 * The agent's `answer` frames are uneven: one carries a single table row, the next a whole
 * paragraph that turns up after a pause while a tool ran. Rendered as they land, short lines
 * tick out while a long paragraph appears in one jump, and the reply reads as glitchy - fast in
 * one place, stalled in the next. So AiChatConversationStore holds what has arrived and reveals
 * it at a steady pace, one unit at a time. This file decides what a unit is.
 *
 * WHY UNITS AND NOT CHARACTERS
 * ----------------------------
 * The reply is Markdown, re-rendered on every update. Cut mid-construct, the renderer shows the
 * raw syntax until the rest arrives: `[HAMPTON Cobbl` with its bracket, a URL typing itself out,
 * `**Subtotal` with its asterisks, a table row with half its cells, a `-` that is not a bullet
 * yet. So a unit is a word, except where a word would leave the Markdown broken:
 *   - a link, a bold/italic/strikethrough run or a code span is revealed whole;
 *   - a table row is revealed whole, and a header row together with the |---| row under it,
 *     which is what makes it a table rather than a line of pipes;
 *   - a short `**Label:** value` line is revealed whole, so a totals block does not flick
 *     between a paragraph and the aligned grid renderParagraph() makes of it;
 *   - a list, heading or quote marker goes out with the first word after it;
 *   - a fenced code block is revealed whole.
 * A unit that has not fully arrived waits for more text - unless the stream has finished, in
 * which case whatever is left is shown as it stands.
 */

/** The most a single unit costs against the reveal pace, however long its source is. */
export const MAX_REVEAL_UNIT_COST = 20;

/**
 * The end of the next unit after `from` - the point the reply can be shown up to.
 *
 * A unit carries the whitespace in front of it, so a space or a line break is never revealed on
 * its own (it changes nothing on screen) and never left dangling at the end of what is shown.
 *
 * @param text everything the stream has delivered so far
 * @param from how much of it is already on screen
 * @param isFinal the stream has closed, so nothing is still arriving
 * @returns the end index of the next unit, or `from` when that unit has to wait for more text
 */
export function nextRevealEnd(text: string, from: number, isFinal: boolean): number {
  let start = from;
  while (start < text.length && isWhitespace(text[start])) {
    start++;
  }
  if (start >= text.length) {
    return isFinal ? text.length : from;
  }

  if (isAtLineStart(text, start)) {
    const lineUnit = readLineUnit(text, start, isFinal);
    if (lineUnit !== null) {
      return lineUnit === WAIT ? from : lineUnit;
    }
    start = skipBlockMarker(text, start);
  }

  const end = readWord(text, start, isFinal);
  return end === WAIT ? from : end;
}

/**
 * What one unit costs against the reveal pace: its length, capped. Without the cap a table row
 * or a link - mostly URL - would cost ten words and hold the reveal up for a visible beat.
 */
export function revealCost(from: number, end: number): number {
  return Math.min(Math.max(end - from, 1), MAX_REVEAL_UNIT_COST);
}

/**
 * The total cost of everything queued after `from`, up to the first unit still waiting on text.
 * The pace is set from this, so the reveal keeps up with however much is queued.
 */
export function measureRevealBacklog(text: string, from: number, isFinal: boolean): number {
  let cost = 0;
  let index = from;
  while (index < text.length) {
    const end = nextRevealEnd(text, index, isFinal);
    if (end <= index) {
      // A unit is still arriving - count it once, so its wait does not read as an empty queue.
      return cost + MAX_REVEAL_UNIT_COST;
    }
    cost += revealCost(index, end);
    index = end;
  }
  return cost;
}

/** Returned by the readers below for a unit that has not fully arrived yet. */
const WAIT = -1;

/** A list, heading or blockquote marker - revealed with the first word after it. */
const BLOCK_MARKER = /^(#{1,6}[ \t]+|[-*+][ \t]+|\d+[.)][ \t]+|>[ \t]?)/;

/** A whole link, label and target, from its opening bracket. */
const LINK = /\[[^\]\n]*\]\([^)\n]*\)/y;

/** A `**Label:**` totals line is revealed whole only up to this length - past it, it is prose. */
const MAX_LABELLED_LINE_LENGTH = 100;

/** The paired inline markers, longest first so `***` is not read as `**` and a stray `*`. */
const PAIRED_MARKERS = ['***', '**', '~~', '`'];

/**
 * A unit that has to be a whole line, or null when the line starting at `start` is ordinary
 * text and is revealed a word at a time.
 */
function readLineUnit(text: string, start: number, isFinal: boolean): number | null {
  const lineEnd = lineEndAt(text, start);
  const line = text.slice(start, lineEnd);
  const isComplete = lineEnd < text.length || isFinal;

  if (line.startsWith('```')) {
    return readFence(text, lineEnd, isFinal);
  }

  if (hasCellBreak(line)) {
    if (!isComplete) {
      return WAIT;
    }
    // A row under another row is a body row. Anything else may be a header, which only becomes
    // a table once its delimiter row is under it - shown alone, it renders as a line of pipes
    // and then jumps into a table a beat later. So a header waits for, and goes out with, the
    // line after it.
    return isPreviousLineTableRow(text, start) ? lineEnd : readHeaderRow(text, lineEnd, isFinal);
  }

  if (line.length <= MAX_LABELLED_LINE_LENGTH && /^\*\*[^*\n]*:/.test(line)) {
    return isComplete ? lineEnd : WAIT;
  }

  return null;
}

/**
 * A possible table header ending at `lineEnd`: taken together with the delimiter row under it
 * when there is one, or on its own when the next line shows there is not.
 */
function readHeaderRow(text: string, lineEnd: number, isFinal: boolean): number {
  if (lineEnd >= text.length) {
    return lineEnd;
  }

  const nextStart = lineEnd + 1;
  const nextEnd = lineEndAt(text, nextStart);
  const next = text.slice(nextStart, nextEnd);
  const isNextComplete = nextEnd < text.length || isFinal;

  if (isNextComplete) {
    return isDelimiterRow(next) ? nextEnd : lineEnd;
  }
  // The next line is still arriving. Nothing of it yet, or only the characters a delimiter row
  // is made of, and it could still turn this line into a table header.
  return /^\s*\|?[\s:|-]*$/.test(next) ? WAIT : lineEnd;
}

/** A fenced code block, from its opening fence line through its closing one. */
function readFence(text: string, openingLineEnd: number, isFinal: boolean): number {
  const close = text.indexOf('\n```', openingLineEnd);
  if (close === -1) {
    return isFinal ? text.length : WAIT;
  }
  const closeEnd = lineEndAt(text, close + 1);
  return closeEnd < text.length || isFinal ? closeEnd : WAIT;
}

/**
 * One word, with any inline construct inside it taken whole - a bold run or a link can span
 * several words, and is never cut part-way.
 */
function readWord(text: string, start: number, isFinal: boolean): number {
  let index = start;
  while (index < text.length && !isWhitespace(text[index])) {
    const construct = readInlineConstruct(text, index, isFinal);
    if (construct === WAIT) {
      // Show what came before the construct, if anything; the construct waits.
      return index > start ? index : WAIT;
    }
    index = construct ?? index + 1;
  }

  // A word running into the end of what has arrived may not be finished - the next frame can
  // carry the rest of it.
  if (index >= text.length && !isFinal) {
    return WAIT;
  }
  // Can equal `start` only after a marker with nothing behind it on its line (a bare "- "),
  // which is revealed as it stands rather than holding up everything after it.
  return index;
}

/**
 * The end of the inline construct opening at `index`, WAIT if it is still arriving, or null
 * when the character there opens nothing and is ordinary text.
 *
 * Whether a construct is still arriving is judged by its line: once the line is complete, an
 * opener with no closer on it never will have one, and is shown as the literal character the
 * renderer will leave it as.
 */
function readInlineConstruct(text: string, index: number, isFinal: boolean): number | null {
  const character = text[index];
  if (character !== '[' && character !== '*' && character !== '_' && character !== '~'
    && character !== '`') {
    return null;
  }

  const lineEnd = lineEndAt(text, index);
  const isLineComplete = lineEnd < text.length || isFinal;

  if (character === '[') {
    LINK.lastIndex = index;
    const link = LINK.exec(text);
    if (link) {
      return index + link[0].length;
    }
    return isLineComplete ? null : WAIT;
  }

  const marker = PAIRED_MARKERS.find((candidate) => text.startsWith(candidate, index));
  if (marker) {
    const close = text.indexOf(marker, index + marker.length);
    if (close > index + marker.length && close < lineEnd) {
      return close + marker.length;
    }
    return isLineComplete ? index + marker.length : WAIT;
  }

  // A single `*` or `_` is emphasis only where it opens a word - the same test renderInline()
  // applies. Mid-word it is a literal character (an underscore in a code or a file name).
  if ((character === '*' || character === '_') && !isWordCharacter(text[index - 1])) {
    const close = text.indexOf(character, index + 1);
    if (close > index + 1 && close < lineEnd) {
      return close + 1;
    }
    return isLineComplete ? null : WAIT;
  }

  return null;
}

/** Steps over a list, heading or quote marker at the start of a line. */
function skipBlockMarker(text: string, start: number): number {
  const marker = BLOCK_MARKER.exec(text.slice(start, lineEndAt(text, start)));
  return marker ? start + marker[0].length : start;
}

/** True when only indentation sits between the previous line break and `index`. */
function isAtLineStart(text: string, index: number): boolean {
  for (let cursor = index - 1; cursor >= 0; cursor--) {
    if (text[cursor] === '\n') {
      return true;
    }
    if (text[cursor] !== ' ' && text[cursor] !== '\t') {
      return false;
    }
  }
  return true;
}

function isPreviousLineTableRow(text: string, start: number): boolean {
  const lineBreak = text.lastIndexOf('\n', start - 1);
  if (lineBreak === -1) {
    return false;
  }
  const previous = text.slice(text.lastIndexOf('\n', lineBreak - 1) + 1, lineBreak);
  return hasCellBreak(previous);
}

/** The same delimiter test the renderer's isTableStart() applies: every cell `---`/`:--:`. */
function isDelimiterRow(line: string): boolean {
  if (!hasCellBreak(line)) {
    return false;
  }
  const cells = line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|');
  return cells.every((cell) => /^\s*:?-+:?\s*$/.test(cell));
}

/** An unescaped `|` - the renderer's own containsSeparator() test. */
function hasCellBreak(line: string): boolean {
  return /(^|[^\\])\|/.test(line);
}

function lineEndAt(text: string, index: number): number {
  const lineBreak = text.indexOf('\n', index);
  return lineBreak === -1 ? text.length : lineBreak;
}

function isWhitespace(character: string): boolean {
  return character === ' ' || character === '\n' || character === '\t' || character === '\r';
}

function isWordCharacter(character: string | undefined): boolean {
  return !!character && /\w/.test(character);
}
